import { type ReactNode } from "react"

interface CenteredHeroStat {
  /** Icon in the stat box */
  icon: ReactNode
  /** Bold number/value */
  value: string
  /** Label below the value */
  label: string
}

interface CenteredHeroProps {
  /** Breadcrumb component or node above the title */
  breadcrumb?: ReactNode
  /** Title text — wrap a word in <span> for accent coloring */
  title: ReactNode
  /** Subtitle paragraph */
  subtitle?: string
  /** Stat counters below the subtitle */
  stats?: CenteredHeroStat[]
  /** Trust badges (simple text strings with checkmarks) */
  trustBadges?: string[]
  /** Additional classes on the wrapper */
  className?: string
}

export function CenteredHero({
  breadcrumb,
  title,
  subtitle,
  stats,
  trustBadges,
  className = "",
}: CenteredHeroProps) {
  return (
    <div className={`text-center max-w-3xl mx-auto ${className}`}>
      {breadcrumb && <div className="mb-6">{breadcrumb}</div>}

      <h1 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight mb-3 [&>span]:text-[#ff9951]">
        {title}
      </h1>

      {subtitle && (
        <p className="text-sm text-gray-500 leading-relaxed max-w-2xl mx-auto mb-6">
          {subtitle}
        </p>
      )}

      {stats && stats.length > 0 && (
        <div className="flex items-center justify-center gap-8 mb-5">
          {stats.map((stat, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0469f0]/10 flex items-center justify-center flex-shrink-0">
                {stat.icon}
              </div>
              <div className="text-left">
                <p className="text-sm font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {trustBadges && trustBadges.length > 0 && (
        <div className="flex items-center justify-center gap-4 flex-wrap">
          {trustBadges.map((badge, i) => (
            <span key={i} className="flex items-center gap-1.5 text-xs text-gray-500">
              <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {badge}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
